/**
 * useDeepLinkListener — wires Capacitor App plugin `appUrlOpen` events to
 * react-router navigation. Mount once at the App root.
 *
 * Architecture:
 *   - The actual `App.addListener('appUrlOpen', ...)` lives in this hook
 *     (separation of concerns: bootstrap handles boot tasks, the hook handles
 *     React-side wiring with access to `useNavigate`).
 *   - On cold start (`App.getLaunchUrl()`), the hook also drains the launch
 *     URL exactly once after mount.
 *   - Idempotent: tracks processed URLs in a `Set` keyed by `url + timestamp`
 *     so duplicate events from the OS (rare but observed Android < API 30)
 *     don't trigger double navigation.
 *   - No-op on web: `Capacitor.isNativePlatform()` short-circuits early. Web
 *     uses native browser routing for HTTPS URLs; custom scheme is irrelevant.
 *
 * Why not in `mobileBootstrap`?
 *   - The bootstrap module runs BEFORE React mounts, so it can't `useNavigate`.
 *     The hook lives inside the React tree and gets the navigator hook safely.
 *   - The bootstrap module already wires the back button (a one-shot install)
 *     and could in principle queue a launch URL for later, but doing both jobs
 *     here keeps deep-link logic in one file. Bootstrap stays focused on splash
 *     + status bar + back button.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { parseDeepLink, type ParsedDeepLink } from '../lib/deepLinks';

/**
 * Subscribe to `appUrlOpen` + cold-start launch URL. Idempotent: calling the
 * hook multiple times in dev (HMR) is safe — the cleanup removes the listener.
 */
export function useDeepLinkListener(): void {
  const navigate = useNavigate();
  /** Processed URLs (last 16) — guards against duplicate events from OS. */
  const processedRef = useRef<Set<string>>(new Set());

  // react-doctor false-positive: cleanup IS returned at the bottom of this
  // effect via `cancelled` flag + `removeFn` captured in the async `wire()`
  // closure (assigned lines below near the addListener call). The scanner cannot
  // trace closure-captured cleanup from `void wire()` and assumes no cleanup.
  // react-doctor-disable-next-line effect-needs-cleanup, react-doctor/effect-needs-cleanup
  useEffect(() => {
    // Web is a no-op: HTTPS deep links are routed by the browser already.
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let removeFn: (() => void) | undefined;

    const handle = (parsed: ParsedDeepLink): void => {
      // Idempotency: same route+params+source → ignore.
      const key = `${parsed.source}::${parsed.route}::${JSON.stringify(parsed.params)}`;
      if (processedRef.current.has(key)) return;
      processedRef.current.add(key);
      // Cap memory: keep only the most recent 16 entries.
      if (processedRef.current.size > 16) {
        const first = processedRef.current.values().next().value;
        if (first !== undefined) processedRef.current.delete(first);
      }
      // Navigate. Pass params as state so target page can read them via
      // `useLocation().state` if it cares (e.g. `?focus=:id` highlight).
      navigate(parsed.route, { state: parsed.params });
    };

    const wire = async (): Promise<void> => {
      try {
        const { App } = await import('@capacitor/app');
        // 1. Subscribe to runtime URL opens.
        const handler = await App.addListener('appUrlOpen', (event) => {
          const parsed = parseDeepLink(event.url);
          if (!parsed) {
            if (import.meta.env.DEV) {
              console.warn('[useDeepLinkListener] rejected', event.url);
            }
            return;
          }
          if (cancelled) return;
          handle(parsed);
        });
        if (cancelled) {
          handler.remove().catch(() => undefined);
          return;
        }
        removeFn = (): void => {
          handler.remove().catch(() => undefined);
        };

        // 2. Drain cold-start launch URL once.
        const launch = await App.getLaunchUrl();
        if (cancelled) return;
        if (launch?.url) {
          const parsed = parseDeepLink(launch.url);
          if (parsed) handle(parsed);
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[useDeepLinkListener] wiring failed', err);
        }
      }
    };

    void wire();

    return () => {
      cancelled = true;
      if (removeFn) removeFn();
    };
    // navigate is stable per react-router-dom v6; deps intentionally empty
    // beyond it to ensure the listener installs exactly once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
